/**
 * 站外跳转打开脉脉App 的组件
 * 支持微信场景内直接跳转，并可附带剪贴板归因；
 * 微信Android 中使用微信开放标签打开App, 其他情况需实现jumpOpenApp的回调（一般是打开show_open中间页，ios下已支持show_open 路径的universal link）。
 * 注意：使用微信开放标签的前提是，需要在页面中调用过 wx.config 做好配置。
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import copy from 'copy-to-clipboard';
import qs from 'querystring';
import { voyagerTrack } from '@shared/voyager';
import MMDialog from '../MMDialog';

export interface OpenLaunchAppProps {
  // 子元素用于显示按钮UI
  children: React.ReactNode;
  // 外层样式
  style?: React.CSSProperties;
  // 点击按钮时回调，可以进行打点（无论用不用微信开放标签，都会有该回调）
  onClick?: () => void;
  // 微信开放标签里需要跳转的脉脉scheme url
  wxSchemeUrl?: string;
  // 不用微信开放标签跳转时的打开App回调，一般用该回调来进行打开show_open 页面
  jumpOpenApp?: () => void;
  // 是否写入剪贴板做归因分析，默认true
  writeClipboard?: boolean;
  // 需要写入剪贴板的归因key，key 由业务定义
  clipboardKey?: string;
  // 不使用用微信开放标签，脉芽动态配置的情况下，当站外不需要跳站内时，可以使用
  disableWxTag?: boolean;
  // wxbtn的高度（为了处理点击区域的问题） 例如 100px string类型
  height?: string;
  // 是否自动显示打开app的确认弹窗
  showConfirm?: boolean;
  // 点击弹窗确认后的回调
  onConfirm?: () => void;
}

const OpenLaunchApp: React.FC<OpenLaunchAppProps> = (props) => {
  const { style, children, writeClipboard = true, clipboardKey = '', jumpOpenApp, showConfirm, onConfirm } = props;

  // 判断是否是ios环境
  const [isIos, setIsIos] = useState(false);
  // 判断是否为微信环境
  const [isWechat, setIsWechat] = useState(false);
  const [wxSchemeUrl, setWxSchemeUrl] = useState('');
  const [showJudgeModal, setShowJudgeModal] = useState(false);

  const wxRef = useRef(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const CreateMainKey = (str) => {
    // 获取剪切板相关参数
    const { gid, share_euid = '0', share_uid = '', fid = '' } = qs.parse(window.location.search.substr(1));

    // 针对key做兼容处理
    // 拼接 mainkey
    return ~str.indexOf('***') ? str : `${str}***${fid || gid || 0}***${share_uid || share_euid || 0}`;
  };

  const openApp = useCallback(() => {
    if (writeClipboard && clipboardKey) {
      // 剪切板格式
      const clipboardText = `clip_$0:/###${CreateMainKey(clipboardKey)}###${Math.floor(+new Date() / 1000)} $12`;
      copy(clipboardText);
    }

    if (jumpOpenApp) {
      jumpOpenApp();
    } else {
      let UniversalLink = 'https://taou.cn/show_open';

      if (window.location.href.indexOf('app=social') >= 0) {
        UniversalLink = 'https://taou.cn/social_open';
      }
      // 不传jumpOpenApp 的默认情况下，跳至脉脉 App 中以 webview 打开本页面
      window.location.href = `${UniversalLink}?type=openwebview&id=${encodeURIComponent(window.location.href)}`;
    }
  }, [clipboardKey, jumpOpenApp, writeClipboard]);

  const handleClick = () => {
    voyagerTrack('launch_app_click', {});
    props.onClick?.();
    openApp();
  };

  const handleConfirm = () => {
    voyagerTrack('launch_app_confirm_click', {});
    setShowJudgeModal(false);
    onConfirm?.();
    openApp();
  };

  useEffect(() => {
    // android 微信环境下，不弹弹窗
    if (showConfirm && !(isWechat && !isIos)) {
      timerRef.current = setTimeout(() => {
        setShowJudgeModal(true);
      }, 500);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [openApp, onConfirm, showConfirm, isWechat, isIos, props]);

  useEffect(() => {
    // 默认的微信开放标签 scheme，会在脉脉中用webview打开（为啥两次encode?，不清楚）
    const defaultSchemaUrl = `taoumaimai://openwebview?id=${encodeURIComponent(
      encodeURIComponent(window.location.href)
    )}`;
    const schemaUrl = props.wxSchemeUrl || defaultSchemaUrl;
    setWxSchemeUrl(schemaUrl);

    const ua = window.navigator.userAgent;
    const isWechat = /MicroMessenger/i.test(ua);
    const isIos = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
    // 判断环境
    setIsIos(isIos);
    // 判断是否为微信环境
    setIsWechat(isWechat);
  }, [props.wxSchemeUrl]);

  useEffect(() => {
    if (isWechat && !isIos && wxRef.current) {
      wxRef.current.addEventListener('ready', function () {
        voyagerTrack('growth_fission_ui_show', {
          type: 'wx_open_btn',
          main_key: clipboardKey,
        });
      });

      wxRef.current.addEventListener('launch', function () {
        if (writeClipboard && clipboardKey) {
          // 剪切板格式
          const clipboardText = `clip_$0:/###${CreateMainKey(clipboardKey)}###${Math.floor(+new Date() / 1000)} $12`;
          copy(clipboardText);
        }
        voyagerTrack('growth_fission_btn_click', {
          type: 'wx_open_btn',
          main_key: clipboardKey,
        });
        voyagerTrack('launch_app_click', {});
        props.onClick?.();
      });

      wxRef.current.addEventListener('error', function (e) {
        const err = JSON.stringify(e.detail);
        voyagerTrack('growth_fission_btn_click', {
          type: 'wx_open_btn_err',
          main_key: clipboardKey,
          err,
        });
        if (props.jumpOpenApp) {
          props.jumpOpenApp();
        } else {
          // 不传jumpOpenApp 的默认情况下，跳至脉脉 App 中以 webview 打开
          window.location.href = `https://taou.cn/show_open?type=openwebview&id=${encodeURIComponent(
            window.location.href
          )}`;
        }
      });
    }
  }, [isWechat, isIos, clipboardKey, writeClipboard, props]);

  if (isWechat && !isIos && !props.disableWxTag) {
    return (
      <div style={{ ...style, position: 'relative' }}>
        {children}
        {/*  @ts-ignore */}
        <wx-open-launch-app
          ref={wxRef}
          // 这是向微信注册的脉脉 appid
          appid='wxe0ce239cac5643b4'
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
          }}
          extinfo={`{"schema":"${wxSchemeUrl}"}`}
        >
          <script type='text/wxtag-template'>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
          </script>
          {/*  @ts-ignore */}
        </wx-open-launch-app>
      </div>
    );
  }

  return (
    <div style={style} onClick={handleClick}>
      {children}
      <MMDialog
        visible={showJudgeModal}
        onRequestClose={() => {
          setShowJudgeModal(false);
        }}
        okText='允许'
        onOk={handleConfirm}
        cancelText='取消'
        title=''
        containerStyle={{
          bottom: 'calc(50vh - 85px)',
        }}
      >
        即将离开，打开“脉脉”
      </MMDialog>
    </div>
  );
};

export default React.memo(OpenLaunchApp);
